import { Product } from "@b2bportal/purchase-api";
import {
  AirCancelForAnyReasonStateV2,
  AirPriceDropState,
  BaggageActions,
  BaggageEventTypes,
  BaggageSelectionState,
  CartState,
  CartUpdateEventType,
  CartUpdateState,
  FlightActionTypes,
  FlightPassengerActionTypesV2,
  FlightPassengerEventTypesV2,
  GenericChildState,
  getAirCancelForAnyReasonMachineV2,
  getAirDisruptionMachineV1,
  getAirPriceDropMachine,
  getBaggageMachine,
  getCartFulfillMachine,
  getCartQuoteMachine,
  getCartUpdateMachine,
  getContactInformationMachine,
  getFlightPassengerInformationMachineV2,
  getFlightResumeMachineState,
  getPassportMachineState,
  getReviewMachineState,
  getSeatMachine,
  ParentState,
  PassengerInformationChildState,
  PassengerInformationState,
  ResumeEvent,
  ResumeSharedActionTypes,
  SeatEventTypes,
  SeatSelectionState,
  trackPollFailure,
  VipSupportActionTypes,
  VipSupportEventTypes,
} from "@hopper-b2b/checkout";
import { COMPLETE_BUY_AIR } from "@hopper-b2b/types";
import type { MachineConfig } from "xstate";

import { NubankPaymentEvent } from "../../checkout";
import {
  BagsAndSeatsEventTypes,
  getNubankPaymentMachine,
} from "../../checkout/states";
import { NubankPaymentActionTypes } from "../../checkout/states/payments/actions";
import {
  NubankPaymentChildState,
  NubankPaymentState,
} from "../../checkout/states/payments/types";
import { ActionTypes } from "./actions";
import { Event, type TEvent } from "./events";
import { GuardTypes } from "./guards";
import { getInitialFlightMachineContext } from "./initialContext";
import { ServiceTypes } from "./services";
import { getApplyFeesMachine } from "./states/apply-fees";
import type { FlightMachineContext } from "./types";

export const flightMachineId = "nubank-flight-checkout";
export const flightStateMachine: MachineConfig<
  FlightMachineContext,
  any,
  TEvent
> = {
  schema: {
    context: getInitialFlightMachineContext(),
    events: {} as TEvent,
  },

  // Machine identifier
  id: flightMachineId,

  // Initial state
  initial: ParentState.resume,

  // State definitions

  states: {
    [ParentState.resume]: getFlightResumeMachineState({
      parentMachineId: flightMachineId,
      nextState: `#${flightMachineId}.${ParentState.loading}`,
    }),
    [ParentState.loading]: {
      entry: ActionTypes.setInitialContactInformation,
      always: [
        {
          target: `#${flightMachineId}.${ParentState.passengerInformation}`,
          cond: GuardTypes.contactContinue,
        },
        {
          target: `#${flightMachineId}.${ParentState.contactInformation}`,
        },
      ],
    },
    [ParentState.contactInformation]: {
      ...getContactInformationMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        nextStateFromReview: `#${flightMachineId}.${ParentState.cartQuote}`,
      }),
    },
    [ParentState.passengerInformation]: {
      ...getFlightPassengerInformationMachineV2({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartQuote}`,
        nextStateFromReview: `#${flightMachineId}.${ParentState.cartQuote}`,
        previousState: `#${flightMachineId}.${ParentState.contactInformation}`,
        stateOverride: {
          [PassengerInformationState.passengerPicker]: {
            states: {
              // New state used only to display the policy warning modal.
              infantSeatNotSupportedWarning: {
                on: {
                  [Event.DISMISS_INFANT_SEAT_NOT_SUPPORTED_WARNING]: {
                    target: `#${flightMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
                  },
                },
              },
            },
            on: {
              // Forked from the shared PassengerInformationV2 machine
              [FlightPassengerEventTypesV2.SELECT_PASSENGER]: [
                {
                  actions: [
                    FlightPassengerActionTypesV2.resetValidated,
                    FlightPassengerActionTypesV2.selectPassenger,
                  ],
                  target: `#${flightMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.infantSeatNotSupportedWarning`,
                  cond: GuardTypes.shouldShowInfantSeatNotSupportedWarning,
                },
                {
                  actions: [
                    FlightPassengerActionTypesV2.resetValidated,
                    FlightPassengerActionTypesV2.selectPassenger,
                  ],
                  target: `#${flightMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.validate}`,
                  cond: GuardTypes.willHaveSelectedPassengers,
                },
                {
                  actions: FlightPassengerActionTypesV2.selectPassenger,
                },
              ],
            },
          },
        },
      }),
    },
    [ParentState.passport]: {
      ...getPassportMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
      }),
    },
    [ParentState.cartQuote]: {
      entry: [FlightActionTypes.addMultiProviderProduct],
      ...getCartQuoteMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.applyFees}`,
      }),
    },
    [ParentState.applyFees]: {
      ...getApplyFeesMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.disruptionV1}`,
      }),
    },
    [ParentState.disruptionV1]: {
      ...getAirDisruptionMachineV1({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cancelForAnyReasonV2}`,
      }),
    },
    [ParentState.cancelForAnyReasonV2]: {
      ...getAirCancelForAnyReasonMachineV2({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.priceDrop}`,
      }),
    },
    // Add Products to Cart
    [ParentState.priceDrop]: {
      ...getAirPriceDropMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.bagsAndSeats`,
        stateOverride: {
          [AirPriceDropState.route]: {
            always: [
              {
                cond: GuardTypes.isFlightNotHackerfare,
                target: `#${flightMachineId}.${ParentState.priceDrop}.${AirPriceDropState.addProduct}`,
              },
              {
                target: `#${flightMachineId}.bagsAndSeats`,
              },
            ],
          },
        },
      }),
    },
    bagsAndSeats: {
      initial: "bagsAndSeatsRoute",
      entry: [
        ActionTypes.clearCartUpdateError,
        ActionTypes.setSeatMapResponse,
        ActionTypes.resetSeatSelectionComplete,
        ActionTypes.initSelectedBaggage,
        ActionTypes.setCheckedBagsPrices,
        // Re-associate selections with current passengers when entering add-ons
        // This handles the case where passenger changed from review screen
        ActionTypes.reassociateBagsWithCurrentPassengers,
        ActionTypes.reassociateSeatsWithCurrentPassengers,
      ],
      states: {
        bagsAndSeatsRoute: {
          always: [
            {
              target: "bagsAndSeatsLoading",
              cond: GuardTypes.isBaggageAndSeatsEligible,
            },
            {
              target: `#${flightMachineId}.${ParentState.bagsSelection}`,
            },
          ],
        },
        bagsAndSeatsLoading: {
          invoke: {
            src: ServiceTypes.fetchSeatMapService,
            onDone: {
              actions: ActionTypes.setSeatMap,
              target: "bagsAndSeatsIdle",
            },
            onError: {
              target: "bagsAndSeatsIdle",
            },
          },
        },
        bagsAndSeatsIdle: {
          on: {
            [BagsAndSeatsEventTypes.SELECT_BAGGAGE]: {
              target: `#${flightMachineId}.${ParentState.bagsSelection}`,
            },
            [BagsAndSeatsEventTypes.SELECT_SEAT]: {
              target: `#${flightMachineId}.${ParentState.seatSelection}`,
            },
            [BagsAndSeatsEventTypes.NEXT]: {
              target: `#${flightMachineId}.${ParentState.cartUpdate}`,
            },
            [BagsAndSeatsEventTypes.PREVIOUS]: {
              target: `#${flightMachineId}.${ParentState.passengerInformation}`,
            },
          },
        },
      },
    },
    [ParentState.bagsSelection]: {
      ...getBaggageMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.seatSelection}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        stateOverride: {
          [BaggageSelectionState.route]: {
            always: [
              {
                cond: GuardTypes.isBaggageEligible,
                actions: [BaggageActions.trackViewedBaggagePageEvent],
                target: `#${flightMachineId}.${ParentState.bagsSelection}.${GenericChildState.idle}`,
              },
              {
                target: `#${flightMachineId}.${ParentState.seatSelection}`,
              },
            ],
          },
          [BaggageSelectionState.idle]: {
            on: {
              [BaggageEventTypes.SELECT_BAGGAGE]: {
                actions: [
                  ActionTypes.selectBaggage,
                  ActionTypes.resetEditBaggageFromReview,
                ],
              },
              [BaggageEventTypes.NEXT]: [
                {
                  actions: ActionTypes.resetEditBaggageFromReview,
                  target: `#${flightMachineId}.bagsAndSeats`,
                  cond: GuardTypes.isBaggageAndSeatsEligible,
                },
                {
                  actions: ActionTypes.resetEditBaggageFromReview,
                  target: `#${flightMachineId}.${ParentState.seatSelection}`,
                },
              ],
              [BaggageEventTypes.SKIP_BAGGAGE_SELECTION]: {
                actions: [
                  ActionTypes.setSkipBaggageSelection,
                  ActionTypes.resetSelectedBaggage,
                ],
              },
              [BaggageEventTypes.PREVIOUS]: [
                {
                  target: `#${flightMachineId}.bagsAndSeats`,
                  cond: GuardTypes.isBaggageAndSeatsEligible,
                },
                {
                  // Going back before add-ons - reset selections
                  actions: [
                    ActionTypes.resetSelectedSeats,
                    ActionTypes.resetSelectedBaggage,
                  ],
                  target: `#${flightMachineId}.${ParentState.passengerInformation}`,
                },
              ],
            },
          },
        },
      }),
    },
    [ParentState.seatSelection]: {
      ...getSeatMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartUpdate}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        stateOverride: {
          // Override route to skip loading if we already have seat selections (edit mode)
          [SeatSelectionState.route]: {
            always: [
              {
                // Skip to idle if we already have selected seats and seat map HTML
                target: SeatSelectionState.idle,
                cond: GuardTypes.hasExistingSeatSelections,
              },
              {
                target: SeatSelectionState.loading,
                cond: GuardTypes.hasSeatMapAvailability,
              },
              {
                target: `#${flightMachineId}.${ParentState.cartUpdate}`,
              },
            ],
          },
          [SeatSelectionState.idle]: {
            on: {
              [SeatEventTypes.SELECT_SEATS]: {
                actions: [
                  ActionTypes.selectSeat,
                  ActionTypes.resetSkippedSeatSelection,
                ],
              },
              [SeatEventTypes.NEXT]: [
                {
                  actions: [
                    ActionTypes.resetEditSeatsFromReview,
                    ActionTypes.setSeatSelectionComplete,
                  ],
                  target: `#${flightMachineId}.bagsAndSeats`,
                  cond: GuardTypes.isBaggageAndSeatsEligible,
                },
                {
                  actions: [
                    ActionTypes.resetEditSeatsFromReview,
                    ActionTypes.setSeatSelectionComplete,
                  ],
                  target: `#${flightMachineId}.${ParentState.cartUpdate}`,
                },
              ],
              [SeatEventTypes.SKIP_SEAT_SELECTION]: {
                actions: [
                  ActionTypes.setSkipSeatSelection,
                  ActionTypes.resetSelectedSeats,
                  ActionTypes.setSeatSelectionComplete,
                ],
              },
              [SeatEventTypes.PREVIOUS]: [
                {
                  target: `#${flightMachineId}.bagsAndSeats`,
                  cond: GuardTypes.isBaggageAndSeatsEligible,
                },
                {
                  // Going back before add-ons - reset selections
                  actions: [
                    ActionTypes.resetSelectedSeats,
                    ActionTypes.resetSelectedBaggage,
                  ],
                  target: `#${flightMachineId}.${ParentState.passengerInformation}`,
                },
              ],
            },
          },
        },
      }),
    },
    // Update Cart
    [ParentState.cartUpdate]: {
      ...getCartUpdateMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.review}`,
      }),
      exit: FlightActionTypes.checkForPriceChange,
    },
    [ParentState.review]: {
      initial: GenericChildState.loading,
      entry: FlightActionTypes.checkForPriceChange,
      ...getReviewMachineState({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartUpdateBeforeFulfill}`,
        previousState: `#${flightMachineId}.${ParentState.passengerInformation}`,
        stateOverride: {
          [GenericChildState.loading]: {
            target: `#${flightMachineId}.${ParentState.review}.${GenericChildState.idle}`,
          },
          [GenericChildState.idle]: {},
        },
      }),
    },
    [ParentState.cartUpdateBeforeFulfill]: {
      ...getCartUpdateMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.payment}`,
      }),
    },
    [ParentState.payment]: {
      ...getNubankPaymentMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.cartFulfill}`,
        previousState: `#${flightMachineId}.${ParentState.review}`,
      }),
    },
    [ParentState.cartFulfill]: {
      ...getCartFulfillMachine({
        parentMachineId: flightMachineId,
        nextState: `#${flightMachineId}.${ParentState.bookingConfirmation}`,
        trackPollFailureAction: trackPollFailure<FlightMachineContext>({
          eventName: COMPLETE_BUY_AIR,
          getAdditionalProperties: (context) => {
            return {
              multi_ticket_type: context.cartQuote.quoteBreakdown.products.find(
                (product) =>
                  product.product.type === Product.MultiProviderFlight
              )
                ? "hacker_fare" //TODO: add "virtual_interline" when it's ready
                : "single",
              ...context.cancelForAnyReasonV2?.aggregatedShopTrackingProperties
                ?.properties,
            };
          },
        }),
      }),
    },
    [ParentState.bookingConfirmation]: {},
  },
  on: {
    [FlightPassengerEventTypesV2.OPEN_PASSENGER_PICKER]: `#${flightMachineId}.${ParentState.passengerInformation}`,
    [FlightPassengerEventTypesV2.OPEN_PASSPORT_FORM]: `#${flightMachineId}.${ParentState.passport}`,
    [FlightPassengerEventTypesV2.OPEN_FORM_AND_SET_PASSENGER]: {
      actions: FlightPassengerActionTypesV2.setCurrentPassenger,
      target: `#${flightMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerForm}`,
    },

    [VipSupportEventTypes.TOGGLE_SUPPORT_STATUS]: {
      actions: VipSupportActionTypes.toggleVipSupport,
    },
    [NubankPaymentEvent.GO_TO_PAYMENT_ENROLLMENT_SUCCESS]: {
      target: `#${flightMachineId}.${ParentState.payment}.${NubankPaymentState.requestAuth}.${NubankPaymentChildState.success}`,
      actions: NubankPaymentActionTypes.setAuthorizationCode,
    },
    [NubankPaymentEvent.GO_TO_PAYMENT_ENROLLMENT_FAILURE]: {
      target: `#${flightMachineId}.${ParentState.payment}.${NubankPaymentState.requestAuth}.${NubankPaymentChildState.failure}`,
      actions: NubankPaymentActionTypes.setAuthorizationCode,
    },
    [Event.GO_TO_BOOKING_CONFIRMATION]: `#${flightMachineId}.${ParentState.bookingConfirmation}`,
    [Event.GO_TO_SEAT_SELECTION]: [
      {
        target: `#${flightMachineId}.${ParentState.cartQuote}`,
        cond: GuardTypes.shouldRequoteOnNavigation,
      },
      {
        target: `#${flightMachineId}.${ParentState.seatSelection}`,
      },
    ],
    [Event.GO_TO_BAGS_AND_SEATS_SELECTION]: [
      {
        target: `#${flightMachineId}.${ParentState.cartQuote}`,
        cond: GuardTypes.shouldRequoteOnNavigation,
      },
      {
        target: `#${flightMachineId}.bagsAndSeats`,
      },
    ],
    [Event.EDIT_SEATS_FROM_REVIEW]: {
      actions: ActionTypes.setEditSeatsFromReview,
      target: `#${flightMachineId}.${ParentState.seatSelection}`,
    },
    [FlightPassengerEventTypesV2.CLEAR_PASSENGER_INFORMATION_ERROR]: {
      actions: FlightPassengerActionTypesV2.clearPassengerInformationError,
      target: `#${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}`,
    },
    [CartUpdateEventType.RETRY_CART_UPDATE]: {
      actions: [ActionTypes.clearCartUpdateError],
      target: `#${flightMachineId}.${CartState.cartUpdate}.${CartUpdateState.update}`,
    },
    // Events below are used for browser navigation
    [Event.GO_TO_PASSENGER_SELECT]: `#${flightMachineId}.${ParentState.passengerInformation}.${PassengerInformationState.passengerPicker}.${PassengerInformationChildState.idle}`,
    [Event.GO_TO_CONTACT_INFORMATION]: `#${flightMachineId}.${ParentState.contactInformation}`,
    [Event.GO_TO_CART_QUOTE]: `#${flightMachineId}.${ParentState.cartQuote}`,
    [Event.GO_TO_REVIEW]: `#${flightMachineId}.${ParentState.review}`,
    [Event.GO_TO_PAYMENT]: `#${flightMachineId}.${ParentState.payment}`,
    [Event.GO_TO_PRICE_DROP]: `#${flightMachineId}.${ParentState.priceDrop}`,
    [Event.GO_TO_PAYMENT_SELECT_CREDIT_OR_DEBIT]: `#${flightMachineId}.${ParentState.payment}.${NubankPaymentState.selectCreditOrDebit}`,
    [Event.GO_TO_PAYMENT_SELECT_CREDIT]: `#${flightMachineId}.${ParentState.payment}.${NubankPaymentState.credit}`,
    [Event.GO_TO_PAYMENT_SELECT_REVIEW]: `#${flightMachineId}.${ParentState.payment}.${NubankPaymentState.review}`,
    [Event.DELETE_BAGS_FROM_CART_UPDATE]: {
      actions: [ActionTypes.removeBaggageFromCartUpdate],
      target: `#${flightMachineId}.${ParentState.cartUpdate}`,
    },
    [AirCancelForAnyReasonStateV2.initialize]: {
      actions: ActionTypes.setCfarState,
    },
    [SeatEventTypes.SET_SEATS_CONTEXT]: {
      actions: ActionTypes.setSeatsContext,
    },
    // Handle price drop context update from any state
    [ResumeEvent.SET_PRICE_DROP_CONTEXT]: {
      actions: ResumeSharedActionTypes.setPriceDropContext,
    },
  },
};
